<script setup>
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';

const { t } = useI18n();
const store = useStore();

const props = defineProps({
  campaigns: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['viewAnalytics', 'edit']);

const repeatTypeLabel = repeatType => {
  const key = (repeatType || 'once').toUpperCase();
  return t(`CAMPAIGN.V2.REPEAT_TYPES.${key}`);
};

const statusLabel = status => {
  const key = (status || 'active').toUpperCase();
  return t(`CAMPAIGN.V2.STATUS.${key}`);
};

const statusClass = status => {
  const classes = {
    active: 'bg-n-teal-3 text-n-teal-11',
    completed: 'bg-n-blue-3 text-n-blue-11',
    pending: 'bg-n-amber-3 text-n-amber-11',
    processing: 'bg-n-amber-3 text-n-amber-11',
    failed: 'bg-n-red-3 text-n-red-11',
  };
  return classes[status] || 'bg-n-alpha-3 text-n-slate-11';
};

const triggerCampaign = async campaign => {
  try {
    await store.dispatch('campaignsV2/trigger', campaign.id);
  } catch {
    // handled by store
  }
};

const deleteCampaign = async campaign => {
  if (window.confirm(t('CAMPAIGN.CONFIRM_DELETE.TITLE'))) {
    await store.dispatch('campaignsV2/delete', campaign.id);
  }
};
</script>

<template>
  <div class="overflow-x-auto">
    <table class="w-full text-sm">
      <thead>
        <tr class="border-b border-n-weak">
          <th class="px-3 py-3 text-left font-medium text-n-slate-11">
            {{ t('CAMPAIGN.V2.LIST.TITLE') }}
          </th>
          <th class="px-3 py-3 text-left font-medium text-n-slate-11">
            {{ t('CAMPAIGN.V2.LIST.INBOX') }}
          </th>
          <th class="px-3 py-3 text-left font-medium text-n-slate-11">
            {{ t('CAMPAIGN.V2.LIST.AUDIENCE') }}
          </th>
          <th class="px-3 py-3 text-left font-medium text-n-slate-11">
            {{ t('CAMPAIGN.V2.LIST.STATUS') }}
          </th>
          <th class="px-3 py-3 text-left font-medium text-n-slate-11">
            {{ t('CAMPAIGN.V2.LIST.REPEAT_TYPE') }}
          </th>
          <th class="px-3 py-3 text-right font-medium text-n-slate-11">
            {{ t('CAMPAIGN.V2.LIST.TOTAL_SENT') }}
          </th>
          <th class="px-3 py-3 text-right font-medium text-n-slate-11">
            {{ t('CAMPAIGN.V2.LIST.ACTIONS') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="campaign in campaigns"
          :key="campaign.id"
          class="border-b border-n-weak hover:bg-n-alpha-1 transition-colors"
        >
          <td class="px-3 py-3 font-medium text-n-slate-12">
            {{ campaign.title }}
          </td>
          <td class="px-3 py-3 text-n-slate-11">
            {{ campaign.inbox?.name }}
          </td>
          <td class="px-3 py-3 text-n-slate-11">
            {{ campaign.audience_name || '-' }}
          </td>
          <td class="px-3 py-3">
            <span
              class="px-2 py-1 text-xs font-medium rounded-full"
              :class="statusClass(campaign.campaign_status)"
            >
              {{ statusLabel(campaign.campaign_status) }}
            </span>
          </td>
          <td class="px-3 py-3 text-n-slate-11">
            {{ repeatTypeLabel(campaign.repeat_type) }}
          </td>
          <td class="px-3 py-3 text-right text-n-slate-11">
            {{ campaign.total_sent_count || 0 }}
          </td>
          <td class="px-3 py-3 text-right">
            <div class="flex items-center justify-end gap-2">
              <button
                class="px-2 py-1 text-xs font-medium rounded bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3"
                @click="emit('viewAnalytics', campaign)"
              >
                {{ t('CAMPAIGN.V2.LIST.VIEW_ANALYTICS') }}
              </button>
              <button
                class="px-2 py-1 text-xs font-medium rounded bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3"
                @click="emit('edit', campaign)"
              >
                {{ t('CAMPAIGN.V2.LIST.EDIT') }}
              </button>
              <button
                class="px-2 py-1 text-xs font-medium rounded bg-n-red-3 text-n-red-11 hover:bg-n-red-4"
                @click="deleteCampaign(campaign)"
              >
                {{ t('CAMPAIGN.V2.LIST.DELETE') }}
              </button>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
